import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AutoSizer, MultiGrid } from 'react-virtualized'
import {
  PREVIEW_CHUNK_ROW_COUNT,
  createPreviewStore,
  loadPreviewCsvRowsRange,
  getValidationIssueCellKeySet,
  getValidationIssueColumnLevelSet,
  matchesValidationRowFilter,
  isValidationCellHighlighted
} from '../extra/helpers'
import { PREVIEW_EMPTY_CELL_LABEL } from '../extra/constants'
import { useGenericMappingDispatch, useGenericMappingSelector } from '../extra/store'
import { ErrorIcon, WarningIcon, ValidationMappingLegend, getValidationMappingCellTitle } from './ValidationStatusIcons'

const TABLE_CELL_WIDTH = 200
const LOAD_MORE_THRESHOLD = 20
const OVERSCAN_ROWS = 8
const OVERSCAN_COLS = 2
const ROW_NUMBER_COLUMN_WIDTH = 100

/**
 * @param {{
 *   taskKey: string
 *   highlightedCell?: { rowNumber?: number | string | null; columnName?: string | null } | null
 *   onErrorCellClick?: (rowNumber?: number | string | null, columnName?: string | null) => void
 * }} props
 * @returns {React.ReactElement | null}
 */
const PreviewCsvTable = ({ taskKey, highlightedCell, onErrorCellClick }) => {
  const dispatch = useGenericMappingDispatch()
  // マッピング結果CSVパネル全体（ページ縦スクロールで画面内に入れる対象）
  const panelRef = useRef(null)
  // 逆方向連動で「該当行がまだ未ロード」の場合に、ロード完了後スクロールするための保留ターゲット
  const pendingScrollCellRef = useRef(null)
  // 仮想テーブルで中央表示する行の index（filteredPreviewEntries 基準。MultiGrid の scrollToRow に渡す）
  const [scrollToRowIndex, setScrollToRowIndex] = useState(-1)
  // 仮想テーブルで中央表示する列の index（previewCsvHeaders 基準。MultiGrid の scrollToColumn に渡す）
  const [scrollToColumnIndex, setScrollToColumnIndex] = useState(-1)
  const previewCsvStatus = useGenericMappingSelector((s) => s.previewCsvStatus)
  const previewCsvHeaders = useGenericMappingSelector((s) => s.previewCsvHeaders ?? [])
  const previewCsvRows = useGenericMappingSelector((s) => s.previewCsvRows ?? [])
  const previewCsvRowCount = useGenericMappingSelector((s) => s.previewCsvRowCount ?? 0)
  const previewCsvError = useGenericMappingSelector((s) => s.previewCsvError)
  const currentFilter = useGenericMappingSelector((s) => s.currentFilter)
  const errorDetailsList = useGenericMappingSelector((s) => s.errorDetailsList ?? [])
  const warningDetailsList = useGenericMappingSelector((s) => s.warningDetailsList ?? [])
  const isLoadingMoreRef = useRef(false)

  const validationRowErrorSet = useMemo(() => {
    return getValidationIssueCellKeySet(errorDetailsList)
  }, [errorDetailsList])

  const validationRowWarningSet = useMemo(() => {
    return getValidationIssueCellKeySet(warningDetailsList)
  }, [warningDetailsList])

  // 行に紐づかない列単位エラー/警告（区分マスター未設定など）は列全体をハイライトする
  const validationColumnLevelErrorSet = useMemo(() => {
    return getValidationIssueColumnLevelSet(errorDetailsList)
  }, [errorDetailsList])

  const validationColumnLevelWarningSet = useMemo(() => {
    return getValidationIssueColumnLevelSet(warningDetailsList)
  }, [warningDetailsList])

  const filteredPreviewEntries = useMemo(() => {
    const rows = previewCsvRows ?? []
    const entries = rows.map((row, i) => ({ rowNumber: i + 1, row }))
    return entries.filter((e) =>
      matchesValidationRowFilter(e.rowNumber, currentFilter, errorDetailsList, warningDetailsList)
    )
  }, [previewCsvRows, currentFilter, errorDetailsList, warningDetailsList])

  const loadMoreRows = useCallback(async () => {
    if (!taskKey || previewCsvStatus !== 'loaded') return
    if (isLoadingMoreRef.current || previewCsvRows.length >= previewCsvRowCount) return

    isLoadingMoreRef.current = true
    try {
      const fileStore = createPreviewStore(taskKey)
      const nextRows = await loadPreviewCsvRowsRange(
        fileStore,
        previewCsvRows.length,
        previewCsvRows.length + PREVIEW_CHUNK_ROW_COUNT - 1
      )
      if (nextRows.length > 0) {
        dispatch('APPEND_PREVIEW_CSV_ROWS', nextRows)
      } else if (previewCsvRows.length < previewCsvRowCount) {
        // これ以上チャンクが無いのに total 行数だけ大きい場合の無限ロード防止
        dispatch('SET_PREVIEW_CSV_DATA', { rowCount: previewCsvRows.length })
      }
    } finally {
      isLoadingMoreRef.current = false
    }
  }, [dispatch, previewCsvRowCount, previewCsvRows.length, previewCsvStatus, taskKey])

  useEffect(() => {
    if (previewCsvStatus !== 'loaded') return
    if (currentFilter === 'all') return
    if (previewCsvRows.length >= previewCsvRowCount) return
    if (filteredPreviewEntries.length > 0) return
    loadMoreRows().catch(() => {
      dispatch('SET_PREVIEW_CSV_ERROR', 'マッピング結果CSVの追加読み込みに失敗しました。')
    })
  }, [
    currentFilter,
    filteredPreviewEntries.length,
    previewCsvRowCount,
    previewCsvRows.length,
    previewCsvStatus,
    dispatch,
    loadMoreRows
  ])

  const handleRowsRendered = useCallback(
    ({ stopIndex }) => {
      if (previewCsvRows.length === 0) return
      if (previewCsvRows.length >= previewCsvRowCount) return
      const displayed = filteredPreviewEntries.length
      if (displayed > 0 && stopIndex < displayed - LOAD_MORE_THRESHOLD) return
      loadMoreRows().catch(() => {
        dispatch('SET_PREVIEW_CSV_ERROR', 'マッピング結果CSVの追加読み込みに失敗しました。')
      })
    },
    [dispatch, filteredPreviewEntries.length, loadMoreRows, previewCsvRowCount, previewCsvRows.length]
  )

  // MultiGrid の onSectionRendered はヘッダー行（fixedRowCount=1）込みの行 index を返すため、
  // データ行（filteredPreviewEntries 基準）の index に変換して追加読み込みを判定する
  const handleSectionRendered = useCallback(
    ({ rowStopIndex }) => {
      handleRowsRendered({ stopIndex: rowStopIndex - 1 })
    },
    [handleRowsRendered]
  )

  // 検証詳細一覧の行クリック（逆方向連動）で highlightedCell が変わったとき、
  // 該当セルが画面内に来るよう「ページ縦スクロール → テーブル内スクロール」を実行する。
  // テーブル内の行・列スクロールは MultiGrid の scrollToRow / scrollToColumn で行う。
  // 該当行が未ロードの場合は保留ターゲットに登録し、ロード完了後に別 effect で解決する。
  // 別コンポーネント（検証詳細一覧）起点のクリックであり、かつ react-virtualized の
  // 仮想 DOM に対する命令的スクロールのため、イベントハンドラ／描画時計算では実現できず useEffect を用いる。
  useEffect(() => {
    if (!highlightedCell || previewCsvStatus !== 'loaded') return

    const columnName = highlightedCell.columnName
    if (columnName == null) return

    // 1. マッピング結果CSVパネル全体を画面内へ。
    //    アプリ共通ヘッダーは position:fixed のため、単純な scrollIntoView だと
    //    パネル上部（＝上の方の行）がヘッダーに隠れてしまう。ヘッダー高さ分のオフセットを
    //    差し引いて、パネル全体がヘッダー下に収まるようウィンドウをスクロールする。
    const panel = panelRef.current
    if (panel) {
      const headerHeight = document.getElementById('header')?.offsetHeight ?? 0
      const HEADER_MARGIN = 12
      const panelTop = panel.getBoundingClientRect().top + window.scrollY
      const targetTop = Math.max(0, panelTop - headerHeight - HEADER_MARGIN)
      window.scrollTo({ top: targetTop, behavior: 'smooth' })
    }

    // 2. テーブル内の横スクロールで該当列を画面内へ（列数が多く画面外にある場合の対策）
    const colIndex = previewCsvHeaders.findIndex((header, index) => (header || `column-${index}`) === columnName)
    if (colIndex >= 0) {
      setScrollToColumnIndex(colIndex)
    }

    // 3. 仮想テーブルの行スクロール（列単位エラーは特定行を持たないため対象外）
    if (highlightedCell.columnLevel) {
      pendingScrollCellRef.current = null
      return
    }
    const displayIndex = filteredPreviewEntries.findIndex((e) => e.rowNumber === highlightedCell.rowNumber)
    if (displayIndex >= 0) {
      // 既にロード済み: scrollToRow を更新して中央表示（react-virtualized が中央寄せ・再描画を処理）
      pendingScrollCellRef.current = null
      setScrollToRowIndex(displayIndex)
    } else {
      // 未ロード: 保留ターゲットに登録し、追加ロード完了後に解決 effect でスクロールする
      pendingScrollCellRef.current = highlightedCell
    }
  }, [highlightedCell, previewCsvStatus, previewCsvHeaders, filteredPreviewEntries])

  // 保留中のスクロールターゲットを解決する。
  // 数百行の CSV では該当行がまだチャンク未ロードのことがあるため、行が表示可能になるまで
  // 追加ロードを繰り返し、ロード完了後（filteredPreviewEntries 更新後）に中央スクロールする。
  useEffect(() => {
    const target = pendingScrollCellRef.current
    if (!target || previewCsvStatus !== 'loaded') return

    const displayIndex = filteredPreviewEntries.findIndex((e) => e.rowNumber === target.rowNumber)
    if (displayIndex >= 0) {
      pendingScrollCellRef.current = null
      setScrollToRowIndex(displayIndex)
      return
    }

    // 該当行がまだ未ロードの場合は追加ロードを試みる（全行ロード済みなら諦める）
    if (previewCsvRows.length >= previewCsvRowCount) {
      pendingScrollCellRef.current = null
      return
    }
    loadMoreRows().catch(() => {
      pendingScrollCellRef.current = null
      dispatch('SET_PREVIEW_CSV_ERROR', 'マッピング結果CSVの追加読み込みに失敗しました。')
    })
  }, [filteredPreviewEntries, previewCsvStatus, previewCsvRows.length, previewCsvRowCount, loadMoreRows, dispatch])

  // scrollToRow / scrollToColumn を適用した直後に -1 へ戻す。
  // こうしておくと、同じ行を連続クリックしても（値が同一でも）毎回スクロールが再発火する。
  useEffect(() => {
    if (scrollToRowIndex < 0 && scrollToColumnIndex < 0) return
    const id = requestAnimationFrame(() => {
      setScrollToRowIndex(-1)
      setScrollToColumnIndex(-1)
    })
    return () => cancelAnimationFrame(id)
  }, [scrollToRowIndex, scrollToColumnIndex])

  // 先頭列は「行番号」、以降はデータ列（TABLE_CELL_WIDTH 固定幅）
  const columnWidth = useCallback(({ index }) => (index === 0 ? ROW_NUMBER_COLUMN_WIDTH : TABLE_CELL_WIDTH), [])

  // MultiGrid は列・行の両方を仮想化し、縦/横スクロールバーを
  // コンテナ（ビューポート）端に配置する。react-virtualized の Table は
  // 本体 Grid が「全列幅の単一カラム」構造となり、縦スクロールバーが
  // コンテンツ右端（画面外）に付いてしまうため MultiGrid を使用する。
  // rowIndex=0 はヘッダー行（fixedRowCount=1 で固定）、columnIndex=0 は行番号列。
  const cellRenderer = useCallback(
    ({ columnIndex, rowIndex, key, style }) => {
      const isHeaderRow = rowIndex === 0
      const isRowNumberColumn = columnIndex === 0

      if (isHeaderRow) {
        const label = isRowNumberColumn ? '行番号' : previewCsvHeaders[columnIndex - 1]
        return (
          <div
            key={key}
            style={style}
            className="flex items-center !px-2 border border-[#d5d6e5] bg-[#e0e5f2] text-[#7476a6] text-sm font-bold"
          >
            <span className="truncate" title={label || '-'}>
              {label || '-'}
            </span>
          </div>
        )
      }

      const entry = filteredPreviewEntries[rowIndex - 1]
      const rowNumber = entry?.rowNumber ?? rowIndex

      if (isRowNumberColumn) {
        return (
          <div
            key={key}
            style={style}
            className="flex items-center !px-2 border border-[#d5d6e5] bg-white text-sm"
            title={String(rowNumber)}
          >
            {rowNumber}
          </div>
        )
      }

      const index = columnIndex - 1
      const dataKey = previewCsvHeaders[index] || `column-${index}`
      const cellData = entry?.row?.[index] ?? ''
      const cellKey = `${rowNumber}-${dataKey}`
      const isError = validationRowErrorSet.has(cellKey) || validationColumnLevelErrorSet.has(dataKey)
      const isWarning =
        !isError && (validationRowWarningSet.has(cellKey) || validationColumnLevelWarningSet.has(dataKey))
      const isHighlighted = isValidationCellHighlighted(highlightedCell, rowNumber, dataKey)
      const isEmpty = cellData === undefined || cellData === null || cellData === ''

      const stateClasses = isHighlighted
        ? 'bg-blue-100'
        : isError
          ? 'cursor-pointer bg-red-50 text-red-900'
          : isWarning
            ? 'bg-amber-50 text-amber-900'
            : isEmpty
              ? 'bg-white italic text-[#94a3b8]'
              : 'bg-white'

      const cellTitle = getValidationMappingCellTitle({
        isError,
        isWarning,
        isEmpty,
        rawValue: cellData
      })

      return (
        <div
          key={key}
          style={style}
          className={`flex min-w-0 items-center gap-1.5 !px-2 border border-[#d5d6e5] text-sm ${stateClasses}`}
          title={cellTitle}
          role={isError || isWarning ? 'button' : undefined}
          onClick={() => (isError || isWarning) && onErrorCellClick?.(rowNumber, dataKey)}
        >
          {isError && <ErrorIcon />}
          {!isError && isWarning && <WarningIcon />}
          <span className="min-w-0 truncate">{isEmpty ? PREVIEW_EMPTY_CELL_LABEL : String(cellData)}</span>
        </div>
      )
    },
    [
      filteredPreviewEntries,
      previewCsvHeaders,
      highlightedCell,
      onErrorCellClick,
      validationRowErrorSet,
      validationRowWarningSet,
      validationColumnLevelErrorSet,
      validationColumnLevelWarningSet
    ]
  )

  if (previewCsvStatus === 'idle') return null

  if (previewCsvStatus === 'loading') {
    return (
      <div className="mb-4 rounded-lg border border-[#bfdbfe] bg-[#eff6ff] px-4 py-4">
        <div className="text-sm font-medium text-[#1d4ed8]">マッピング結果CSV</div>
        <p className="mt-1 text-sm text-[#64748b]">署名付きURLからCSVを取得して表示しています...</p>
      </div>
    )
  }

  if (previewCsvError) {
    return (
      <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-4">
        <div className="text-sm font-medium red--text">マッピング結果CSV</div>
        <p className="mt-1 text-sm red--text">{previewCsvError}</p>
      </div>
    )
  }

  if (previewCsvHeaders.length === 0) {
    return (
      <div className="mb-4 rounded-lg border border-[#e2e8f0] bg-[#f8fafc] px-4 py-4 text-sm text-[#64748b]">
        表示可能なプレビュー行がありません。
      </div>
    )
  }

  return (
    <div ref={panelRef} className="mb-2 rounded-lg border border-[#e2e8f0] overflow-hidden">
      <div className="flex items-center justify-between gap-3 px-4 pt-3 pb-2 flex-wrap">
        <h3 className="font-medium">マッピング結果CSV</h3>
        <p className="text-xs text-[#64748b]">
          原本CSVをそのまま表示しています。読み込み済み {previewCsvRows.length.toLocaleString()}/
          {previewCsvRowCount.toLocaleString()} 行
        </p>
      </div>
      <div className="setting-table is-benchmark h-[500px] w-full min-w-0 overflow-hidden relative">
        <div className="h-full w-full min-h-0 min-w-0">
          <AutoSizer>
            {({ height, width }) => (
              <MultiGrid
                // 追加読み込みで行数が増えてもスクロール位置を保つため key に行数は含めない
                // （フィルタ・ヘッダー変更時のみ再マウントして先頭へ戻す）
                key={`${currentFilter}-${previewCsvHeaders.length}`}
                className="setting-table__table"
                fixedRowCount={1}
                fixedColumnCount={1}
                cellRenderer={cellRenderer}
                columnCount={previewCsvHeaders.length + 1}
                columnWidth={columnWidth}
                rowCount={filteredPreviewEntries.length + 1}
                rowHeight={30}
                width={width}
                height={height}
                overscanRowCount={OVERSCAN_ROWS}
                overscanColumnCount={OVERSCAN_COLS}
                onSectionRendered={handleSectionRendered}
                scrollToRow={scrollToRowIndex >= 0 ? scrollToRowIndex + 1 : -1}
                scrollToColumn={scrollToColumnIndex >= 0 ? scrollToColumnIndex + 1 : -1}
                scrollToAlignment="center"
                classNameBottomLeftGrid="border-r border-[#d5d6e5] z-10"
                classNameTopLeftGrid="border-r border-[#d5d6e5] z-10"
              />
            )}
          </AutoSizer>
        </div>
      </div>
      <ValidationMappingLegend />
    </div>
  )
}

export default PreviewCsvTable
